export type PromotionCatalogRef = {
  pool_code: string;
  match_mode?: "INCLUDE" | "EXCLUDE" | string;
  reference_type:
    | "MENU_ITEM"
    | "SELLABLE_VARIANT"
    | "MENU_CATEGORY"
    | "MODIFIER_GROUP"
    | "MODIFIER_OPTION"
    | "VARIANT_OPTION"
    | string;
  reference_id: string;
};

export type PromotionPoolEligibilityRule = {
  pool_code: string;
  rule_code: string;
  params: Record<string, unknown>;
  sort_order?: number;
};

export type PromotionConditionNode = {
  kind: "GROUP" | "PREDICATE";
  operator?: "AND" | "OR";
  children?: PromotionConditionNode[];
  code?: string;
  negate?: boolean;
  params?: Record<string, unknown>;
};

export type PromotionBenefit = {
  code: string;
  params: Record<string, unknown>;
};

export type PromotionRuntimeSnapshot = {
  runtime_schema_version: number;
  organization_id: string;
  organization_timezone?: string;
  policy_id: string;
  policy_code: string;
  policy_name: string;
  promotion_version_id: string;
  version_no: number;
  lifecycle_status: string;
  activation_code: "AUTO" | "STAFF_CLAIM" | "CLAIM_CODE" | "MEMBER_ENTITLEMENT" | string;
  audience_mode?: "PUBLIC" | "ACTIVE_MEMBER_REQUIRED" | string;
  audience_mode_source?: "ORGANIZATION_POLICY" | "PROMOTION_VERSION" | string;
  manual_claim_selection_limit?: number;
  organization_promotion_policy_version_id?: string | null;
  rule_schema_version: number;
  condition_document: PromotionConditionNode;
  benefit_document: PromotionBenefit[];
  schedule_document: {
    days_of_week?: number[];
    time_windows?: Array<{ start: string; end: string }>;
  };
  limit_document: {
    order_discount_cap?: number;
    max_applications_per_order?: number;
    [key: string]: unknown;
  };
  stacking_mode: "STACKABLE" | "EXCLUSIVE" | "BEST_DEAL" | string;
  stack_group_code: string | null;
  conflict_scope?: "ORDER" | "LINE" | "UNIT" | string;
  priority_rank: number;
  loyalty_earn_treatment: "FULL_POINTS" | "HALF_POINTS" | "NO_POINTS" | string;
  offline_capability: "OFFLINE_SAFE" | "OFFLINE_RISK_LIMITED" | "ONLINE_REQUIRED" | string;
  offline_policy_document: Record<string, unknown>;
  effective_from: string | null;
  effective_to: string | null;
  branch_ids: string[];
  commerce_channel_ids: string[];
  catalog_refs: PromotionCatalogRef[];
  pool_eligibility_rules?: PromotionPoolEligibilityRule[];
};

export type PromotionRuntimeDeltaEvent = {
  revision: number;
  event_type: "UPSERT" | "TOMBSTONE";
  policy_id: string;
  promotion_version_id: string;
  runtime_snapshot: PromotionRuntimeSnapshot;
  created_at: string;
};

export type PromotionBootstrapPayload = {
  ok: boolean;
  contract: string;
  projection_contract?: string;
  organization_id: string;
  branch_id: string;
  commerce_channel_id: string;
  node_id?: string;
  revision: number;
  promotions: PromotionRuntimeSnapshot[];
};

export type PromotionDeltaPayload = {
  ok: boolean;
  contract: string;
  projection_contract?: string;
  organization_id: string;
  branch_id: string;
  commerce_channel_id: string;
  node_id?: string;
  since_revision: number;
  upper_bound_revision: number;
  next_revision: number;
  has_more: boolean;
  events: PromotionRuntimeDeltaEvent[];
};

type GatewayEnvelope<T> = { ok: boolean; data?: T; error?: string };
export type PromotionRuntimeGatewayResult<T> = { ok: boolean; data: T | null; error: string | null };

type ClientOptions = { baseUrl: string; timeoutMs?: number };

export class PosPromotionRuntimeGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: ClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 12_000;
  }

  bootstrap(input: { deviceSecret: string; sessionToken: string }) {
    return this.post<PromotionBootstrapPayload>({
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      since_revision: 0,
      limit: 500,
    });
  }

  delta(input: { deviceSecret: string; sessionToken: string; sinceRevision: number; limit?: number }) {
    return this.post<PromotionDeltaPayload>({
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      since_revision: input.sinceRevision,
      limit: input.limit ?? 500,
    });
  }

  private async post<T>(body: unknown): Promise<PromotionRuntimeGatewayResult<T>> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/promotion`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "omit",
        cache: "no-store",
        signal: controller.signal,
      });
      const payload = (await response.json().catch(() => null)) as GatewayEnvelope<T> | null;
      if (!response.ok || !payload?.ok || !payload.data) {
        return { ok: false, data: null, error: payload?.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      return { ok: true, data: payload.data, error: null };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error instanceof DOMException && error.name === "AbortError"
          ? "POS_PROMOTION_RUNTIME_TIMEOUT"
          : error instanceof Error
            ? error.message
            : "POS_PROMOTION_RUNTIME_LOAD_FAILED",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
